// src/components/admin/AdminEvenements.jsx
// Tous les événements : activer / désactiver, supprimer (si aucune réservation),
// reporter, annuler (rembourse les acheteurs).

import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import {
    getEvenementsAdmin, basculerEvenement, supprimerEvenement, reporterEvenement, annulerEvenementAdmin,
} from "../../services/AdminGestionService";
import { useListeAdmin, TableAdmin, Pagination, Recherche, Filtre, EnTete, Badge, Action, BoutonActualiser, euros, dateFr } from "./AdminUI";

const statutEvenement = (e) => {
    if (e.annule_at) return <Badge couleur="rouge">Annulé</Badge>;
    if (!Number(e.is_active)) return <Badge couleur="orange">Désactivé</Badge>;
    if (new Date(String(e.dateEven).replace(" ", "T")) < new Date()) return <Badge couleur="gris">Passé</Badge>;
    return <Badge couleur="vert">En ligne</Badge>;
};

const AdminEvenements = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const createurId = params.get("createur_id") || "";

    const L = useListeAdmin(getEvenementsAdmin, { q: "", statut: "", billetterie: "", createur_id: createurId });
    const [message, setMessage] = useState(null);

    const agir = async (fn, confirmation) => {
        if (confirmation && !window.confirm(confirmation)) return;
        try {
            const r = await fn();
            setMessage({ ok: r.success !== false, texte: r.message });
            L.recharger();
        } catch (e) {
            setMessage({ ok: false, texte: e.message });
        }
    };

    const reporter = (e) => {
        const d = window.prompt(`Nouvelle date pour « ${e.nomEven} » (AAAA-MM-JJ HH:MM) :`, String(e.dateEven).slice(0, 16));
        if (d) agir(() => reporterEvenement(e.id, d));
    };

    const annuler = (e) => {
        const c = window.prompt(
            `Annuler « ${e.nomEven} » ?\n${e.nb_reservations} réservation(s) seront remboursées (hors frais de paiement) et prévenues par e-mail.\n\nMotif (visible par les acheteurs) :`
        );
        if (c !== null) agir(() => annulerEvenementAdmin(e.id, c));
    };

    const colonnes = [
        { cle: "id", titre: "#", classe: "text-gray-400 text-xs" },
        { cle: "nomEven", titre: "Événement", rendu: (e) => (
            <div className="max-w-[220px]">
                <a href={`/evenement/${e.id}`} target="_blank" rel="noreferrer" className="font-semibold text-gray-900 hover:text-violet-700 line-clamp-2">{e.nomEven}</a>
                <div className="text-[11px] text-gray-500">{e.villeEven || "—"}</div>
            </div>
        )},
        { cle: "dateEven", titre: "Date", rendu: (e) => <span className="text-xs whitespace-nowrap">{dateFr(e.dateEven, true)}</span> },
        { cle: "createur", titre: "Créateur", rendu: (e) => <span className="text-xs text-gray-600">{e.createur_email || "—"}</span> },
        { cle: "billetterie_type", titre: "Billetterie", rendu: (e) =>
            e.billetterie_type === "thrundrz" ? <Badge couleur="violet">Thrundrz</Badge>
                : e.billetterie_type === "externe" ? <Badge couleur="gris">Externe</Badge>
                : <Badge couleur="bleu">{Number(e.isGratuit) ? "Gratuit" : "Aucune"}</Badge> },
        { cle: "nb_reservations", titre: "Réservations", classe: "text-center", rendu: (e) => (
            <button onClick={() => navigate(`/admin/reservations?evenement_id=${e.id}`)} className="font-semibold text-violet-700 hover:underline">
                {e.nb_reservations}{e.nbr_places_max ? <span className="text-gray-400 font-normal"> / {e.nbr_places_max}</span> : null}
            </button>
        )},
        { cle: "ventes", titre: "Ventes", classe: "text-right", rendu: (e) => Number(e.ventes) > 0 ? euros(e.ventes) : <span className="text-gray-300">—</span> },
        { cle: "statut", titre: "Statut", rendu: statutEvenement },
        { cle: "actions", titre: "Actions", rendu: (e) => (
            <div className="flex flex-wrap gap-0.5">
                {!e.annule_at && (
                    <Action couleur={Number(e.is_active) ? "orange" : "vert"}
                        onClick={() => agir(() => basculerEvenement(e.id), Number(e.is_active) ? `Désactiver « ${e.nomEven} » ? Il disparaît de l'application, les billets restent valables.` : null)}>
                        {Number(e.is_active) ? "Désactiver" : "Activer"}
                    </Action>
                )}
                {!e.annule_at && Number(e.nb_reservations) > 0 && (
                    <>
                        <Action onClick={() => reporter(e)}>Reporter</Action>
                        <Action couleur="rouge" onClick={() => annuler(e)}>Annuler</Action>
                    </>
                )}
                <Action couleur="rouge"
                    disabled={Number(e.nb_reservations_total) > 0}
                    title={Number(e.nb_reservations_total) > 0 ? "Impossible : des réservations existent. Désactivez-le." : "Supprimer définitivement"}
                    onClick={() => agir(() => supprimerEvenement(e.id), `Supprimer définitivement « ${e.nomEven} » ?`)}>
                    Supprimer
                </Action>
            </div>
        )},
    ];

    return (
        <div className="max-w-7xl mx-auto">
            <EnTete titre="Événements" sousTitre={`${L.page?.total ?? "…"} événement(s)`}
                actions={<>
                    <Recherche valeur={L.filtres.q} onChange={(v) => L.setFiltre("q", v)} placeholder="Nom, ville, e-mail créateur…" />
                    <Filtre valeur={L.filtres.statut} onChange={(v) => L.setFiltre("statut", v)}
                        options={[["", "Tous"], ["a_venir", "À venir"], ["passes", "Passés"], ["inactifs", "Désactivés"], ["annules", "Annulés"]]} />
                    <Filtre valeur={L.filtres.billetterie} onChange={(v) => L.setFiltre("billetterie", v)}
                        options={[["", "Toutes billetteries"], ["thrundrz", "Thrundrz"], ["externe", "Externe"], ["aucune", "Aucune"]]} />
                    <BoutonActualiser onClick={L.recharger} />
                    <button onClick={() => navigate("/admin/evenements/ajouter")}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-xs font-bold" style={{ backgroundColor: "#481699" }}>
                        <PlusCircle size={14} /> Créer
                    </button>
                </>}
            />

            {createurId && (
                <div className="mb-4 flex items-center gap-2 text-xs">
                    <Badge couleur="violet">Créateur : {params.get("nom") || `#${createurId}`}</Badge>
                    <button className="text-gray-500 underline" onClick={() => { setParams({}); L.setFiltre("createur_id", ""); }}>retirer le filtre</button>
                </div>
            )}

            {message && (
                <div className={`mb-4 px-4 py-2.5 rounded-xl text-sm border ${message.ok ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                    {message.texte}
                </div>
            )}

            <TableAdmin colonnes={colonnes} lignes={L.page?.data} loading={L.loading} erreur={L.erreur} />
            <Pagination page={L.page} onPage={L.allerA} onParPage={L.setParPage} />
        </div>
    );
};

export default AdminEvenements;